import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { AUTH } from '@/constants/testIds';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = await login(formData.email, formData.password);
      toast.success('Login successful!');
      
      // Navigate based on role
      if (user.role === 'ADMIN' || user.role === 'SUPER_ADMIN') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md space-y-8">
          <div>
            <h2 className="text-4xl font-heading font-bold text-brand-primary">
              WellingtonCDFS
            </h2>
            <p className="mt-2 text-slate-600">Sign in to your account</p>
          </div>

          <form data-testid={AUTH.loginForm} onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email">Email address</Label>
              <Input
                data-testid={AUTH.emailInput}
                id="email"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="mt-1"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                data-testid={AUTH.passwordInput}
                id="password"
                type="password"
                required
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="mt-1"
                placeholder="••••••••"
              />
            </div>

            <Button
              data-testid={AUTH.submitBtn}
              type="submit"
              className="w-full"
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </Button>
          </form>

          <div className="text-center text-sm">
            <span className="text-slate-600">Don't have an account? </span>
            <Link
              data-testid={AUTH.registerLink}
              to="/register"
              className="font-semibold text-brand-accent hover:underline"
            >
              Sign up
            </Link>
          </div>

          <div className="text-center pt-4 border-t border-slate-200">
            <Link to="/" data-testid="login-back-home" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-brand-accent">
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:block lg:w-1/2 bg-gradient-to-br from-brand-primary to-brand-secondary relative">
        <div className="absolute inset-0 flex items-center justify-center p-12 text-white">
          <div className="max-w-lg">
            <h3 className="text-4xl font-bold mb-6">Trade with Confidence</h3>
            <p className="text-xl text-slate-200">
              Access global markets with professional trading tools and real-time data.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;